/**
 * `<RolodexApp>` — CODEX's content: the campaign's people, places, powers and vessels as Union
 * paperwork, with the LANCERS (the players' pilots) first.
 *
 * **One list, one reader** (`decisions/0205`). The index on the left ({@link RolodexSpine}) is
 * the only list. With nothing open the reader shows a brief: the most-cited files and a count
 * by class that filters the index (F2). With a file open it shows that file. Both live under
 * one `/codex/rolodex/<id>` path segment, and "show the brief" is what happens when the id
 * resolves to nothing — a deliberate {@link ROLODEX_LIST} and a dangling deep link render the
 * same.
 *
 * **One payload, one rendering.** `content` is world content over `GET /api/world/codex`
 * (`useWorldCodex`), the same for every viewer: no reveal state and no per-viewer projection
 * (`decisions/0160`). Issuing a record to the table is an edit to `<world>/codex.json`
 * (`codex/README.md`). The content-only lookups live in `codexView.ts`; this file paints them.
 *
 * ── THE LANCERS ──
 *
 * `lancers` is the roster's player-owned pilots (`useLancers`), joined here to the `person`
 * entry naming each one's `pilot` (`lancers.ts`). A filed lancer's entry leaves the PERSONS
 * group and reads as the lancer's personnel file: the same dossier form as any NPC, with the
 * pilot's own background quoted as IN THEIR OWN WORDS and an OPEN IN SQUAD button. A lancer
 * with no entry opens a designed no-record state (F7) whose last line depends on the viewer:
 * the GM gets the entry to write, the owning player gets EDIT IN PILOT, anyone else a note.
 *
 * ── PLACES REPLACE THE CHART ──
 *
 * LAST KNOWN LOCATION opens the place's own file, and NAMED IN lists the files that link to
 * this one (F5). CHART.app and its map-pin jumps are gone (`decisions/0203` point 10).
 */
import { useState, type ReactNode } from "react";
import { faBars, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../../lib/Icon";
import type { CodexContent, CodexEntry, CodexEntryId, CodexFact, CodexKind } from "@lancer-vtt/domain";
import { codexIndex, isBackdropEntry } from "@lancer-vtt/domain";
import { avatarGlyph } from "../avatarGlyph";
import {
  affiliationChip,
  aliasesRow,
  briefFiles,
  crossLinks,
  fileReference,
  firstRecordExcerpt,
  inboundLinks,
  kindTallies,
  locationRow,
  namedIn,
  splitAnnotation,
  splitStamp,
  standingRow,
  type IndexKind,
} from "./codexView";
import { KindGlyph } from "./KindGlyph";
import { findLancerRow, joinLancers, lancerEntrySkeleton, lancerFileReference, mechLine, type Lancer } from "./lancers";
import { RolodexSpine, type SpineRowData } from "./RolodexSpine";
import "./rolodex.css";

/**
 * The `/codex/rolodex/<id>` sentinel meaning "page open, nothing targeted — show the brief."
 * Codex entry ids are hand-authored slugs and pilot ids are uuids, so a bracketed sentinel
 * collides with neither.
 */
export const ROLODEX_LIST = "__rolodex_list__";

/** How each kind labels itself in the file header — the dossier's CLASS field. */
const KIND_LABEL: Record<CodexKind, string> = {
  person: "PERSONNEL FILE",
  place: "NAVIGATIONAL RECORD",
  // A `faction` is a POWER (a polity with territory and standing) and an `org` is a BODY (an
  // outfit acting inside or between them) — see `CodexKind`. The labels say which is which.
  faction: "POLITICAL ASSESSMENT",
  ship: "VESSEL REGISTRY",
  org: "ORGANIZATION FILE",
};
const LANCER_LABEL = "LANCER · PERSONNEL FILE";

/** The brief's subtitle — fixed print-plate copy naming the in-fiction issuing authority and the Line. */
const BRIEF_AUTHORITY = "UNION COLONIAL ADMINISTRATION · RING 10 // CASCADE LINE";

/** One filed fact, classified for the RECORD column: a plain numbered record, or an
 *  `ANNOTATIONS —` fact ({@link splitAnnotation}) lifted out into its own block. */
type ClassifiedFact =
  | { kind: "record"; fact: CodexFact }
  | { kind: "annotation"; fact: CodexFact; byline: string; quote: string };

/** Classifies `facts` in the file's own order — never sorted. */
function classifyFacts(facts: CodexFact[]): ClassifiedFact[] {
  return facts.map((fact) => {
    const annotation = splitAnnotation(fact.text);
    return annotation === null ? { kind: "record", fact } : { kind: "annotation", fact, ...annotation };
  });
}

/**
 * The identity plate — the shell's hexagon + accent-initial device (`--brutalist-hex-clip`), plus
 * the file's class word. A lancer with an uploaded pilot portrait shows it in the hexagon instead;
 * every other file stays procedural (`art` is authored on no entry in the served seed).
 */
function IdentityPlate({ name, classWord, image }: { name: string; classWord: string; image?: string }) {
  return (
    <span className={`rlx-idplate${image ? " rlx-idplate--art" : ""}`}>
      {image ? <img className="rlx-idplate-art" src={image} alt="" draggable={false} /> : <span className="rlx-idplate-init">{avatarGlyph(name)}</span>}
      <span className="rlx-idplate-kd">{classWord}</span>
    </span>
  );
}

/** `☰ INDEX` — opens the index over the reader once the page is too narrow to show both (`<860px`). */
function IndexToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="rlx-fold-toggle" aria-pressed={open} aria-label="Toggle the codex index" onClick={onToggle}>
      <Icon icon={faBars} className="icon-lead" />INDEX
    </button>
  );
}

/** A file chip: opens that file. `named` is the dashed NAMED IN variant. */
function FileChip({ entry, named, onSelect }: { entry: CodexEntry; named?: boolean; onSelect: (id: string) => void }) {
  return (
    <button type="button" className={`rlx-linkchip${named ? " rlx-linkchip--named" : ""}`} title={`Open file — ${entry.name}`} onClick={() => onSelect(entry.id)}>
      {entry.name.toUpperCase()}
    </button>
  );
}

/** Who is reading: the GM, or a player. A lancer's own player is `Lancer.own`. */
export type CodexViewer = "gm" | "player";

/** What a lancer's buttons do; the rail host binds them to navigations. */
export type LancerActions = {
  /** OPEN IN SQUAD: the pilot's page in SQUAD, or in PILOT for the GM (who has no SQUAD). */
  onOpenSquad: (pilotId: string) => void;
  /** EDIT IN PILOT: the owning player's own pilot, in edit mode. */
  onEditInPilot: (pilotId: string) => void;
};

/**
 * The GM's narrative-backdrop pin (`narrative-backdrop` 1.2): a place's file offers SET AS BACKDROP,
 * or CLEAR when it is the one pinned. The host passes it for the GM only; a player's codex gets none.
 */
export type BackdropPin = {
  /** The codex entry pinned as the backdrop, or null. */
  pinned: string | null;
  onPin: (entryId: string | null) => void;
};

export function RolodexApp({
  content,
  lancers,
  viewer,
  actions,
  backdrop,
  viewedEntryId,
  onSelect,
  onBack,
  spineOpen,
  onSpineToggle,
  onSpineClose,
  hostHeight,
}: {
  /** The authored codex, fetched once over `WORLD_CODEX_PATH`. An empty entry list is legal. */
  content: CodexContent;
  /** The roster's lancers, resolved for display (`useLancers`). */
  lancers: readonly Lancer[];
  viewer: CodexViewer;
  actions: LancerActions;
  /** The GM's backdrop pin; absent for a player. */
  backdrop?: BackdropPin;
  /** The open file's id (an entry id, or an unfiled lancer's pilot id), or {@link ROLODEX_LIST}. */
  viewedEntryId: string;
  /** Open a file. */
  onSelect: (entryId: string) => void;
  /** Close the open file, back to the brief. */
  onBack: () => void;
  /** Whether the `<860px` fold shows the index as an open overlay — see `RolodexSpine`. */
  spineOpen: boolean;
  onSpineToggle: () => void;
  onSpineClose: () => void;
  /** `.gms-content`'s measured height, from `CodexApp` — applied as `.rlx-shell`'s real height. */
  hostHeight: number;
}) {
  const [kinds, setKinds] = useState<ReadonlySet<IndexKind>>(new Set());
  const index = codexIndex(content);
  const lancerRows = joinLancers(lancers, content.entries);
  const filed = new Set(lancerRows.flatMap((r) => (r.entry ? [r.entry.id] : [])));
  const rows: SpineRowData[] = [
    ...lancerRows.map((r) => ({
      id: r.key,
      kind: "lancer" as const,
      name: r.lancer.callsign,
      role: r.lancer.name,
      meta: `${r.lancer.player} · ${r.lancer.mech?.name ?? "no mech"}`.toUpperCase(),
      filed: r.entry !== undefined,
      ...(r.lancer.image ? { image: r.lancer.image } : {}),
    })),
    ...content.entries
      .filter((e) => !filed.has(e.id))
      .map((e) => ({ id: e.id, kind: e.kind, name: e.name, ...(e.role !== undefined ? { role: e.role } : {}) })),
  ];

  // A sentinel and a dangling id both resolve to nothing: the brief, per `ROLODEX_LIST`.
  const viewedLancer = findLancerRow(lancerRows, viewedEntryId);
  const viewedEntry = viewedLancer !== undefined ? viewedLancer.entry : content.entries.find((e) => e.id === viewedEntryId);
  const selectedId = viewedLancer?.key ?? viewedEntry?.id ?? null;
  const toggle = <IndexToggle open={spineOpen} onToggle={onSpineToggle} />;

  return (
    <div className="rlx-shell" style={{ height: hostHeight }}>
      <RolodexSpine rows={rows} selectedId={selectedId} onSelect={onSelect} kinds={kinds} onKinds={setKinds} open={spineOpen} onClose={onSpineClose} />
      <div className="rlx-main">
        {selectedId === null ? (
          <Brief content={content} rows={rows} lancerOf={(id) => findLancerRow(lancerRows, id)?.lancer} kinds={kinds} onKinds={setKinds} onSelect={onSelect} toggle={toggle} />
        ) : (
          <FileView
            entry={viewedEntry}
            lancer={viewedLancer?.lancer}
            index={index}
            content={content}
            viewer={viewer}
            actions={actions}
            backdrop={backdrop}
            onSelect={onSelect}
            onBack={onBack}
            toggle={toggle}
          />
        )}
      </div>
    </div>
  );
}

/** SET AS BACKDROP on a place's file, or CLEAR when it is the pinned one. */
function BackdropButton({ entryId, pin }: { entryId: string; pin: BackdropPin }) {
  const pinned = pin.pinned === entryId;
  return (
    <button
      type="button"
      className={`rlx-band-back rlx-band-backdrop${pinned ? " rlx-band-backdrop--on" : ""}`}
      aria-pressed={pinned}
      title={pinned ? "Take this world off the narrative stage" : "Show this world behind every narrative scene"}
      onClick={() => pin.onPin(pinned ? null : entryId)}
    >
      {pinned ? "BACKDROP ▪ CLEAR" : "SET AS BACKDROP"}
    </button>
  );
}

/** The reader with nothing open: the most-cited files and the count by class (F2). */
function Brief({
  content,
  rows,
  lancerOf,
  kinds,
  onKinds,
  onSelect,
  toggle,
}: {
  content: CodexContent;
  rows: readonly SpineRowData[];
  lancerOf: (entryId: string) => Lancer | undefined;
  kinds: ReadonlySet<IndexKind>;
  onKinds: (next: ReadonlySet<IndexKind>) => void;
  onSelect: (entryId: string) => void;
  toggle: ReactNode;
}) {
  const head = (
    <div className="rlx-brief-head">
      <h2 className="rlx-brief-title">Contact registry</h2>
      {toggle}
    </div>
  );
  if (rows.length === 0) {
    return (
      <div className="rlx-brief">
        {head}
        <div className="rlx-empty">
          <div className="rlx-empty-head">NIL RETURN</div>
          <div className="rlx-empty-rule" />
          <div className="rlx-empty-body">No records on file. Contacts, worlds and vessels are filed here as the campaign uncovers them.</div>
        </div>
      </div>
    );
  }
  const brief = briefFiles(content.entries);
  return (
    <div className="rlx-brief">
      {head}
      <div className="rlx-brief-sub">
        {rows.length} ON FILE · {BRIEF_AUTHORITY}
      </div>
      {brief.length > 0 && (
        <section className="rlx-brief-sec" aria-label="Most cited files">
          <div className="rlx-brief-label">
            <span>START HERE · MOST CITED FILES</span>
            <span className="rlx-brief-rule" />
          </div>
          <div className="rlx-brief-cards">
            {brief.map(({ entry, cited }) => {
              const lancer = lancerOf(entry.id);
              return (
                <button key={entry.id} type="button" className="rlx-brief-card" onClick={() => onSelect(entry.id)}>
                  <span className="rlx-brief-card-top">
                    <span className={`rlx-brief-card-glyph${lancer?.image ? " rlx-brief-card-glyph--art" : ""}`}>
                      {lancer?.image ? <img src={lancer.image} alt="" draggable={false} /> : <KindGlyph kind={lancer ? "lancer" : entry.kind} />}
                    </span>
                    <span className="rlx-brief-card-id">
                      <span className="rlx-brief-card-kind">{lancer ? LANCER_LABEL : KIND_LABEL[entry.kind]}</span>
                      <span className="rlx-brief-card-name">{lancer ? lancer.callsign : entry.name}</span>
                    </span>
                  </span>
                  <span className="rlx-brief-card-excerpt">{firstRecordExcerpt(entry)}</span>
                  <span className="rlx-brief-card-foot">
                    CITED IN {cited} {cited === 1 ? "FILE" : "FILES"} · OPEN ›
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
      <section className="rlx-brief-sec" aria-label="Files by class">
        <div className="rlx-brief-label">
          <span>BY CLASS</span>
          <span className="rlx-brief-rule" />
        </div>
        <div className="rlx-brief-tallies">
          {kindTallies(rows).map((t) => {
            const on = kinds.size === 1 && kinds.has(t.kind);
            return (
              <button
                key={t.kind}
                type="button"
                className="rlx-brief-tally"
                aria-pressed={on}
                title={on ? "Show every class in the index" : `Show only ${t.heading.toLowerCase()} in the index`}
                onClick={() => onKinds(on ? new Set() : new Set([t.kind]))}
              >
                <span className="rlx-brief-tally-n">{t.count}</span>
                <span className="rlx-brief-tally-label">{t.heading}</span>
              </button>
            );
          })}
        </div>
      </section>
      <div className="rlx-brief-keys">/ SEARCH · ↑ ↓ BROWSE THE INDEX · ENTER OPENS</div>
    </div>
  );
}

/** One open file: a codex entry, a lancer's filed entry, or a lancer with no entry. */
function FileView({
  entry,
  lancer,
  index,
  content,
  viewer,
  actions,
  backdrop,
  onSelect,
  onBack,
  toggle,
}: {
  entry: CodexEntry | undefined;
  lancer: Lancer | undefined;
  index: Map<CodexEntryId, CodexEntry>;
  content: CodexContent;
  viewer: CodexViewer;
  actions: LancerActions;
  backdrop: BackdropPin | undefined;
  onSelect: (entryId: string) => void;
  onBack: () => void;
  toggle: ReactNode;
}) {
  const title = lancer?.callsign ?? entry?.name ?? "";
  const classWord = lancer ? "LANCER" : KIND_LABEL[entry!.kind].split(" ")[0]!;
  const classLabel = lancer ? LANCER_LABEL : KIND_LABEL[entry!.kind];
  const related = entry ? crossLinks(entry, index) : [];
  const cited = entry ? namedIn(entry, inboundLinks(content.entries)) : [];
  const location = entry ? locationRow(entry, index) : ({ kind: "none" } as const);
  const chip = lancer && entry?.affiliation === undefined ? "UNION · LANCER" : affiliationChip(entry!);

  return (
    <div className="rlx-registry rlx-registry--viewing">
      <div className="rlx-band">
        <button type="button" className="rlx-band-back" onClick={onBack}>
          ‹ BRIEF
        </button>
        <span className="rlx-band-title">{title.toUpperCase()}</span>
        <span className="rlx-band-sub">{classLabel} · READ-ONLY</span>
        {backdrop !== undefined && entry !== undefined && isBackdropEntry(entry) && (
          <BackdropButton entryId={entry.id} pin={backdrop} />
        )}
        {toggle}
      </div>

      <div className="rlx-card">
        {lancer && <LancerHead lancer={lancer} viewer={viewer} onOpenSquad={actions.onOpenSquad} />}
        <div className="rlx-doc">
          {/* The left column: identity plate, the ledger's facts, and the file's links. */}
          <div className="rlx-lcol">
            <div className="rlx-idwrap">
              <IdentityPlate name={title} classWord={classWord} {...(lancer?.image ? { image: lancer.image } : {})} />
              <div className="rlx-idmeta">
                <span className="rlx-chip">{chip}</span>
                <span className="rlx-serial">
                  FILE
                  <br />
                  {lancer ? lancerFileReference(lancer) : fileReference(entry!)}
                </span>
              </div>
            </div>

            <div className="rlx-ledger">
              {lancer && <LedgerRow label="NAME ON FILE">{lancer.name || "—"}</LedgerRow>}
              {entry && <LedgerRow label="STANDING">{standingRow(entry)}</LedgerRow>}
              {entry && <LedgerRow label="ALSO KNOWN AS">{aliasesRow(entry)}</LedgerRow>}
              {lancer && <LedgerRow label="KNOWN FOR (SKILL TRIGGERS)">{lancer.triggers.join(" · ") || "—"}</LedgerRow>}
              {entry && (
                <LedgerRow label="LAST KNOWN LOCATION">
                  {location.kind === "none" ? (
                    "—"
                  ) : (
                    <button type="button" className="rlx-placelink" title={`Open file — ${location.name}`} onClick={() => onSelect(location.entryId)}>
                      {location.name.toUpperCase()} ›
                    </button>
                  )}
                </LedgerRow>
              )}
            </div>

            {related.length > 0 && (
              <div className="rlx-related">
                <div className="rlx-record-label">ASSOCIATED RECORDS</div>
                <div className="rlx-related-chips">
                  {related.map((link) => (
                    <FileChip key={link.id} entry={link} onSelect={onSelect} />
                  ))}
                </div>
              </div>
            )}

            {cited.length > 0 && (
              <div className="rlx-related">
                <div className="rlx-record-label">NAMED IN</div>
                <div className="rlx-related-chips">
                  {cited.map((from) => (
                    <FileChip key={from.id} entry={from} named onSelect={onSelect} />
                  ))}
                </div>
              </div>
            )}

          </div>

          <div className="rlx-rcol">
            {lancer?.background !== undefined && (
              <div className="rlx-ownwords">
                <div className="rlx-ownwords-head">
                  <span>IN THEIR OWN WORDS</span>
                  <span className="rlx-ownwords-by">FROM THE PILOT'S BACKGROUND · WRITTEN BY {lancer.player.toUpperCase()}</span>
                </div>
                <div className="rlx-ownwords-body">{lancer.background}</div>
              </div>
            )}
            {entry ? <Record entry={entry} /> : <NoRecord lancer={lancer!} viewer={viewer} onEditInPilot={actions.onEditInPilot} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function LedgerRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rlx-lrow">
      <span className="rlx-lcell">{label}</span>
      <span className="rlx-lbody">{children}</span>
    </div>
  );
}

/** A lancer file's head: who plays them, and the button to their build. */
function LancerHead({ lancer, viewer, onOpenSquad }: { lancer: Lancer; viewer: CodexViewer; onOpenSquad: (pilotId: string) => void }) {
  return (
    <div className="rlx-lhead">
      <div className="rlx-lhead-id">
        <span className="rlx-lhead-name">{lancer.name || lancer.callsign}</span>
        <span className="rlx-lhead-player">PLAYED BY {lancer.player.toUpperCase()}</span>
      </div>
      <button type="button" className="rlx-squad-open" onClick={() => onOpenSquad(lancer.pilotId)}>
        <Icon icon={faUsers} className="rlx-squad-open-icon" />
        <span className="rlx-squad-open-text">
          <span className="rlx-squad-open-label">{viewer === "gm" ? "OPEN IN PILOT ›" : "OPEN IN SQUAD ›"}</span>
          <span className="rlx-squad-open-sub">{mechLine(lancer)}</span>
        </span>
      </button>
    </div>
  );
}

/** The RECORD column: numbered records in filing order, then any annotations lifted out. */
function Record({ entry }: { entry: CodexEntry }) {
  const classified = classifyFacts(entry.facts);
  const records = classified.filter((c): c is Extract<ClassifiedFact, { kind: "record" }> => c.kind === "record");
  const annotations = classified.filter((c): c is Extract<ClassifiedFact, { kind: "annotation" }> => c.kind === "annotation");
  return (
    <div className="rlx-record">
      <div className="rlx-rec-head">
        <span className="rlx-rec-title">RECORD</span>
        <span className="rlx-rec-order">IN ORDER OF FILING</span>
      </div>
      {entry.facts.length === 0 ? (
        <div className="rlx-record-empty">NO ENTRIES FILED AGAINST THIS RECORD.</div>
      ) : (
        <>
          {records.length > 0 && (
            <ol className="rlx-recs">
              {records.map((record, i) => {
                const { stamp, body } = splitStamp(record.fact.text);
                return (
                  <li key={record.fact.id} className="rlx-rec">
                    <span className="rlx-rec-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="rlx-rec-body">
                      {stamp !== null && <span className="rlx-rec-stamp">{stamp}</span>}
                      <div className="rlx-rec-prose">{body}</div>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
          {/* An ANNOTATIONS fact is speech, not a filed record — its own block, in Archivo. */}
          {annotations.map((annotation) => (
            <div key={annotation.fact.id} className="rlx-annot">
              <div className="rlx-annot-label">ANNOTATIONS</div>
              <div className="rlx-annot-quote">{annotation.quote}</div>
              <div className="rlx-annot-by">
                <span className="rlx-annot-caption">APPENDED OFF-RECORD · NOT COUNTERSIGNED</span>
                <span className="rlx-annot-byline">— {annotation.byline}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * A lancer with no codex entry (F7): a stamp, what the build says, and one line for the viewer —
 * the entry to write for the GM, EDIT IN PILOT for the owning player, a note for anyone else.
 */
function NoRecord({ lancer, viewer, onEditInPilot }: { lancer: Lancer; viewer: CodexViewer; onEditInPilot: (pilotId: string) => void }) {
  return (
    <div className="rlx-norec">
      <div className="rlx-norec-head">
        <span className="rlx-norec-stamp">NO RECORD FILED</span>
        <span className="rlx-norec-lede">Union has a callsign and a build for {lancer.callsign}, and nothing else yet.</span>
      </div>
      <div className="rlx-norec-build">
        <div className="rlx-norec-label">WHAT THE BUILD SAYS</div>
        <div className="rlx-norec-grid">
          <span className="rlx-norec-cell">
            <span className="rlx-norec-k">TALENTS</span>
            {lancer.talents.join(" · ") || "—"}
          </span>
          <span className="rlx-norec-cell">
            <span className="rlx-norec-k">FLIES</span>
            {mechLine(lancer)}
          </span>
          <span className="rlx-norec-cell">
            <span className="rlx-norec-k">LICENSE LEVEL</span>
            LL{lancer.level}
          </span>
        </div>
      </div>
      {viewer === "gm" ? (
        <div className="rlx-norec-gm">
          <span className="rlx-norec-gm-head">GM · WRITE THIS FILE</span>
          <span className="rlx-norec-gm-body">
            A lancer's file is a codex entry naming this pilot. It takes the same parts as any NPC's: a role, a numbered record, annotations, and links to the people and places they've crossed. Add this to the
            served codex file's entries and re-stage it (see codex/README.md):
          </span>
          <code className="rlx-norec-gm-code">{lancerEntrySkeleton(lancer)}</code>
          <span className="rlx-norec-gm-foot">Players never see this box.</span>
        </div>
      ) : lancer.own ? (
        <div className="rlx-norec-own">
          <span>This is your pilot. Your own words go in your background, and they show at the top of this file.</span>
          <button type="button" className="rlx-norec-edit" onClick={() => onEditInPilot(lancer.pilotId)}>
            EDIT IN PILOT ›
          </button>
        </div>
      ) : (
        <div className="rlx-norec-note">The GM files these as the campaign learns who {lancer.callsign} is.</div>
      )}
    </div>
  );
}
